import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { useDispatch } from "react-redux";

import { OAuth as OAuthActions } from "../../js/actions";
import { Context } from "../../js/utils/Context";
import { setSnackbar } from "../../js/reducers/snackbar";
import { setAlertDialog } from "../../js/reducers/alertDialog";

const NAME_REGEX = /^[\w\-\s]+$/;
const YEAR_REGEX = /^\d{4}$/;
const MARKET_REGEX = /^[a-zA-Z\s]+$/;

const ACCENT = "#2563eb";
const ACCENT_DARK = "#1d4ed8";
const TEXT_TITLE = "#0f172a";
const TEXT_PLACEHOLDER = "#94a3b8";
const FIELD_BG = "#f1f5f9";
const ERROR = "#dc2626";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
  },
  title: {
    fontFamily: theme.typography.fontFamily,
    background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
    color: "white",
    padding: theme.spacing(2, 2.5),
    "& h2": {
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.fontWeightSemiBold,
      fontSize: "1rem !important",
      color: "white",
      paddingTop: 3,
    },
  },
  content: {
    padding: theme.spacing(2.5),
    paddingBottom: 0,
    fontFamily: theme.typography.fontFamily,
  },
  fieldGrid: {
    width: "100%",
    margin: 0,
    "& > .MuiGrid-item": {
      paddingTop: "0 !important",
      paddingBottom: "0 !important",
    },
    "& > .MuiGrid-item:nth-last-child(-n+2)": {
      marginBottom: 0,
    },
  },
  fieldGridItem: {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "0 !important",
    paddingRight: "0 !important",
    marginBottom: theme.spacing(1.25),
  },
  fieldGridItemLeft: {
    paddingRight: `${theme.spacing(1)}px !important`,
  },
  fieldGridItemRight: {
    paddingLeft: `${theme.spacing(1)}px !important`,
  },
  fieldLabel: {
    color: TEXT_TITLE,
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.85rem !important",
    fontWeight: theme.typography.fontWeightMedium,
    margin: 0,
    marginBottom: 2,
    padding: 0,
    display: "flex",
    alignItems: "center",
    gap: 4,
    lineHeight: 1.1,
  },
  required: {
    color: ERROR,
    fontWeight: theme.typography.fontWeightSemiBold,
  },
  field: {
    margin: "0 !important",
    "&.MuiFormControl-root": {
      marginTop: "0 !important",
    },
    "& .MuiFilledInput-root": {
      borderRadius: 10,
      backgroundColor: FIELD_BG,
      border: "1px solid transparent",
      fontFamily: theme.typography.fontFamily,
      padding: "0 !important",
      transition:
        "background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
      "&:hover": {
        backgroundColor: "#e9eef5",
      },
      "&.Mui-focused": {
        backgroundColor: "white",
        borderColor: ACCENT,
        boxShadow: `0 0 0 3px rgba(37, 99, 235, 0.15)`,
      },
      "&.Mui-error": {
        borderColor: ERROR,
      },
      "&.Mui-error.Mui-focused": {
        boxShadow: `0 0 0 3px rgba(220, 38, 38, 0.12)`,
      },
      "&::before, &::after": {
        display: "none",
        content: "none",
        border: "none",
      },
    },
    "& .MuiFilledInput-input, & .MuiInputBase-input, & .MuiInputBase-inputHiddenLabel": {
      padding: "14px 12px !important",
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.85rem !important",
      fontWeight: theme.typography.fontWeightRegular,
      color: TEXT_TITLE,
      "&::placeholder": {
        color: TEXT_PLACEHOLDER,
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeightRegular,
        opacity: 1,
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 2,
      marginTop: 2,
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.7rem !important",
      fontWeight: theme.typography.fontWeightRegular,
    },
  },
  actions: {
    padding: theme.spacing(1, 2.5, 1.5, 2.5),
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    "& > :not(:first-child)": {
      marginLeft: 0,
    },
  },
  primaryBtn: {
    textTransform: "none",
    backgroundColor: "#1d4ed8",
    color: "#fff",
    borderRadius: 8,
    padding: "6px 16px",
    minWidth: 84,
    fontSize: "0.85rem !important",
    "&:hover": {
      backgroundColor: "#1e40af",
    },
    "&.Mui-disabled": {
      backgroundColor: "#e2e8f0",
      color: "#94a3b8",
      border: "1px solid #cbd5e1",
    },
  },
  cancelBtn: {
    color: TEXT_TITLE,
    backgroundColor: "white",
    textTransform: "none",
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.85rem !important",
    fontWeight: theme.typography.fontWeightSemiBold,
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    padding: "6px 16px",
    minWidth: 84,
    "&:hover": {
      backgroundColor: "#f8fafc",
      border: "1px solid #94a3b8",
    },
  },
}));

const initialState = {
  modelName: "",
  modelCode: "",
  variant: "",
  yearOfManufacturing: "",
  market: "",
};

const initialErrors = {
  modelName: "",
  modelCode: "",
  variant: "",
  yearOfManufacturing: "",
  market: "",
};

const AddVehicleDialog = ({ open, onClose, onAdded }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [makeSelected] = useContext(Context);

  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState(initialErrors);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setValues(initialState);
      setErrors(initialErrors);
      setSubmitting(false);
    }
  }, [open]);

  const validateField = (field, value) => {
    if (!value) return "";
    switch (field) {
      case "modelName":
        return NAME_REGEX.test(value)
          ? ""
          : "Model Name must contain only alphabets, digits and underscore";
      case "modelCode":
        return NAME_REGEX.test(value)
          ? ""
          : "Model Code must contain only alphabets, digits and underscore";
      case "variant":
        return NAME_REGEX.test(value)
          ? ""
          : "Variant must contain only alphabets, digits and underscore";
      case "yearOfManufacturing":
        return YEAR_REGEX.test(value)
          ? ""
          : "Year of manufacturing must be 4 digits";
      case "market":
        return MARKET_REGEX.test(value)
          ? ""
          : "Market must contain only alphabets and spaces";
      default:
        return "";
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const allFilled = Object.values(values).every((v) => v.trim() !== "");
  const noErrors = Object.values(errors).every((e) => e === "");
  const canSubmit = allFilled && noErrors && !submitting;

  const handleSubmit = async () => {
    if (!makeSelected || !makeSelected.uid) {
      dispatch(
        setSnackbar(
          true,
          "info",
          "Please select an OEM on the dashboard before adding a vehicle."
        )
      );
      return;
    }
    setSubmitting(true);
    try {
      const success = await dispatch(
        OAuthActions.addVehicle(values, makeSelected.uid)
      );
      if (success && success.error === undefined) {
        dispatch(setSnackbar(true, "success", "Add Vehicle Successful"));
        if (onAdded) onAdded();
        onClose();
      } else {
        dispatch(
          setAlertDialog(
            true,
            null,
            "error",
            "Add Vehicle",
            "Add vehicle unsuccessful.\nDetails of failure: " +
            (success && success.message ? success.message : "Unknown error"),
            "OK",
            ""
          )
        );
      }
    } catch (err) {
      dispatch(
        setSnackbar(
          true,
          "error",
          "Unable to add vehicle. Please try again."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h6" component="h2" style={{ fontWeight: 700 }}>
          Add Vehicle
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2} className={classes.fieldGrid}>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemLeft}`}
          >
            <div className={classes.fieldLabel}>
              Model Name <span className={classes.required}>*</span>
            </div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={values.modelName}
              onChange={handleChange("modelName")}
              error={errors.modelName.length !== 0}
              helperText={errors.modelName}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemRight}`}
          >
            <div className={classes.fieldLabel}>
              Model Code <span className={classes.required}>*</span>
            </div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={values.modelCode}
              onChange={handleChange("modelCode")}
              error={errors.modelCode.length !== 0}
              helperText={errors.modelCode}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemLeft}`}
          >
            <div className={classes.fieldLabel}>
              Variant <span className={classes.required}>*</span>
            </div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={values.variant}
              onChange={handleChange("variant")}
              error={errors.variant.length !== 0}
              helperText={errors.variant}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemRight}`}
          >
            <div className={classes.fieldLabel}>
              Year Of Manufacturing{" "}
              <span className={classes.required}>*</span>
            </div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={values.yearOfManufacturing}
              onChange={handleChange("yearOfManufacturing")}
              error={errors.yearOfManufacturing.length !== 0}
              helperText={errors.yearOfManufacturing}
              inputProps={{ maxLength: 4 }}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemLeft}`}
          >
            <div className={classes.fieldLabel}>
              Market <span className={classes.required}>*</span>
            </div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={values.market}
              onChange={handleChange("market")}
              error={errors.market.length !== 0}
              helperText={errors.market}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          className={classes.cancelBtn}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          className={classes.primaryBtn}
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={
            submitting ? (
              <CircularProgress size={16} style={{ color: "white" }} />
            ) : null
          }
        >
          {submitting ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddVehicleDialog;
